import { useField, useForm } from 'vee-validate';
import { computed } from 'vue';
import type { Ref } from 'vue';
import * as yup from 'yup';

import { constant } from '@/constant';
import { useUserStore } from '@/store/userStore';

type SubscribeValues = {
  email: string | null;
};

type ClearableInput = {
  clearValue?: () => void;
};

export function useSubscribeForm(options: {
  onSubscribe: (values: SubscribeValues) => void;
  onUnsubscribe: () => void;
  emailInputRef?: Ref<ClearableInput | null>;
  validationMessages?: {
    invalidEmail: string;
    requiredEmail: string;
  };
}) {
  const userStore = useUserStore();

  const userLoggedIn = computed(() => userStore.isUserLogged);

  const schema = yup.object({
    email: yup.string().when([], {
      is: () => userLoggedIn.value,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) =>
        schema
          .required(options.validationMessages?.requiredEmail ?? 'Email is required.')
          .matches(constant.EmailRegex, options.validationMessages?.invalidEmail ?? 'Enter a valid email address.'),
    }),
  });

  const { handleSubmit, meta, setFieldValue } = useForm({
    initialValues: { email: null },
    validationSchema: schema,
  });

  const { value: emailField, errorMessage } = useField<string | null>('email');

  const subscribe = handleSubmit(async (values) => {
    options.onSubscribe(values);
  });

  const unsubscribe = () => {
    options.onUnsubscribe();
  };

  function resetForm() {
    emailField.value = null;
    setFieldValue('email', null);
    options.emailInputRef?.value?.clearValue?.();
  }

  function updateEmailInput(email: string) {
    setFieldValue('email', email);
    emailField.value = email;
  }

  return {
    emailField,
    errorMessage,
    meta,
    userLoggedIn,
    subscribe,
    unsubscribe,
    resetForm,
    updateEmailInput,
  };
}
